import React, { useState, useEffect } from 'react';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, 
  children, 
  defaultOpen = false,
  className = '',
  isOpen: controlledIsOpen,
  onToggle
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  
  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (onToggle) {
      onToggle(newIsOpen);
    } else {
      setInternalIsOpen(newIsOpen);
    }
  };

  // Inject styles into document head
  useEffect(() => {
    const styleId = 'collapsible-section-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .collapsible-section {
          margin-bottom: 2rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .collapsible-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease;
          text-align: left;
        }

        .collapsible-header:hover {
          background: #e9ecef;
        }

        .collapsible-header h3 {
          margin: 0;
          color: #008F99;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .collapsible-icon {
          color: #6b7280;
          font-size: 0.875rem;
          transition: transform 0.2s ease;
          transform: rotate(-90deg);
        }

        .collapsible-icon.open {
          transform: rotate(0deg);
        }

        .collapsible-content {
          padding: 1.5rem;
          background: white;
          border-top: 1px solid #e5e7eb;
        }

        /* Remove bottom border from form sections inside collapsible */
        .collapsible-content .form-section {
          border-bottom: none;
          padding-bottom: 0;
          margin-bottom: 1.5rem;
        }

        .collapsible-content .form-section:last-child {
          margin-bottom: 0;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div className={`collapsible-section ${className}`}>
      <button
        type="button"
        className="collapsible-header"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <h3>{title}</h3>
        <span className={`collapsible-icon ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </button>
      
      {isOpen && (
        <div className="collapsible-content">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleSection;
